/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import {
  getActiveNotClearedAlerts,
  getAlertsByLevel,
} from "@/presentation/Alert/functions";

import { createSelector } from "reselect";
import { RootState } from "..";
import { t } from "i18next";
import { units, DefaultTimeSeriesPrecision } from "@centralwebteam/narwhal";
import { tidyNumber } from "@/modules/tidyNumber";
import { CarouselDataSet } from "@/store/state";
import constants from "@/constants";
import {
  getDisplayUnit,
  getUnitConverter,
  machineMatchesLocationOrMachineFilter,
  selectAllLocations,
} from "../selectors";

/** Gets the latest unit hints for a specific machine in Current Status */
export const makeSelectLatestUnitHintsForMachine = (machineId: string) =>
  createSelector(
    (state: RootState) => state.currentStatus.machineUnitHints[machineId],
    (hints) => {
      if (!hints || hints.length === 0) return undefined;
      return [...hints]
        .sort((a, b) => a.created.localeCompare(b.created))
        .at(-1);
    }
  );

export const makeSelectLatestCompletedJob = (machineId: string) =>
  createSelector(
    (state: RootState) => state.currentStatus.machineJobSummaries,
    (jobs) => {
      const machineJobs = jobs[machineId];
      return machineJobs
        ? machineJobs.find((job) => job.end !== undefined)
        : undefined;
    }
  );

export const makeSelectLatestJob = (machineId: string) =>
  createSelector(
    (state: RootState) => state.currentStatus.machineJobSummaries,
    (jobs) => {
      const machineJobs = jobs[machineId];
      return machineJobs ? machineJobs[0] : undefined;
    }
  );

export const makeSelectAlertsForDisplayByMachineId = (machineId: string) =>
  createSelector(
    (state: RootState) => state.currentStatus.machineAlerts,
    (state: RootState) => state.filter.active.alertLevel,
    (alerts, activeAlertLevels) =>
      getActiveNotClearedAlerts(
        getAlertsByLevel(alerts[machineId] || [], activeAlertLevels)
      )
  );

export const selectAlertsForActivePopup = createSelector(
  (state: RootState) => state.currentStatus.selectedMachineAlerts,
  (state: RootState) => state.currentStatus.machineAlerts,
  (state: RootState) => state.filter.active.alertLevel,
  (machineId, alerts, activeAlertLevels) =>
    machineId
      ? getActiveNotClearedAlerts(
          getAlertsByLevel(alerts[machineId] || [], activeAlertLevels)
        )
      : []
);

export const makeSelectSparklinesForDisplayByMachineId = (machineId: string) =>
  createSelector(
    (state: RootState) => state.currentStatus.machineCarouselData,
    makeSelectLatestUnitHintsForMachine(machineId),
    (carouselData, unitHints) => {
      const rawData =
        carouselData[machineId]
          ?.slice()
          .sort((a, b) => a.sensor.name.localeCompare(b.sensor.name)) ?? [];

      // Apply unit conversions based on unit hints and each sensor's unitDisplayHint property
      return rawData.map((dataset): CarouselDataSet => {
        const seriesUnit = units.standardise(dataset.sensor.unit);
        const displayUnit = getDisplayUnit(
          dataset.sensor,
          unitHints?.value,
          unitHints?.overrides
        );

        if (displayUnit === seriesUnit) return dataset;

        // Apply unit conversion
        const precision =
          dataset.sensor.displayHints?.displayPrecision ??
          DefaultTimeSeriesPrecision;
        const converter = getUnitConverter(
          dataset.sensor.unitType,
          dataset.sensor.unit,
          displayUnit
        );

        return {
          ...dataset,
          sensor: {
            ...dataset.sensor,
            unit: displayUnit,
          },
          values: dataset.values.map((v) => [
            v[0],
            tidyNumber(converter(Number(v[1])), precision),
            displayUnit,
          ]),
          limits: dataset.limits.map((limit) => ({
            ...limit,
            lowerDisplayLimit:
              limit.lowerDisplayLimit !== undefined &&
              limit.lowerDisplayLimit !== null
                ? converter(limit.lowerDisplayLimit)
                : limit.lowerDisplayLimit,
            upperDisplayLimit:
              limit.upperDisplayLimit !== undefined &&
              limit.upperDisplayLimit !== null
                ? converter(limit.upperDisplayLimit)
                : limit.upperDisplayLimit,
            lowerOperatingLimit:
              limit.lowerOperatingLimit !== undefined &&
              limit.lowerOperatingLimit !== null
                ? converter(limit.lowerOperatingLimit)
                : limit.lowerOperatingLimit,
            upperOperatingLimit:
              limit.upperOperatingLimit !== undefined &&
              limit.upperOperatingLimit !== null
                ? converter(limit.upperOperatingLimit)
                : limit.upperOperatingLimit,
            lowerWarningLimit:
              limit.lowerWarningLimit !== undefined &&
              limit.lowerWarningLimit !== null
                ? converter(limit.lowerWarningLimit)
                : limit.lowerWarningLimit,
            upperWarningLimit:
              limit.upperWarningLimit !== undefined &&
              limit.upperWarningLimit !== null
                ? converter(limit.upperWarningLimit)
                : limit.upperWarningLimit,
          })),
        };
      });
    }
  );

export const selectOpenMachineAlertLogPopup = createSelector(
  (state: RootState) => state.currentStatus.selectedMachineAlerts,
  (state: RootState) => state.global.machines,
  (machine, machines) =>
    machine ? machines.find((_machine) => _machine.id === machine) : undefined
);

export const selectIsMicroView = createSelector(
  (state: RootState) => state.currentStatus.viewMode,
  (viewMode) => viewMode === "MICRO"
);

export const selectMachinesToDisplay = createSelector(
  (state: RootState) => state.filter.active.machineType,
  (state: RootState) => state.filter.active.machineStatus,
  (state: RootState) => state.filter.active,
  (state: RootState) => state.global.machines,
  selectAllLocations,
  (activeMachineTypes, activeMachineStatuses, filters, machines, locations) =>
    machines
      .filter((machine) =>
        machineMatchesLocationOrMachineFilter(machine, filters, locations)
      )
      .filter((machine) =>
        activeMachineTypes.length === 0
          ? true
          : activeMachineTypes.includes(machine.type)
      )
      .filter((machine) =>
        activeMachineStatuses.length === 0
          ? true
          : machine.machineStatus &&
            activeMachineStatuses.includes(machine.machineStatus)
      )
);

export const selectLocationsToDisplay = createSelector(
  (state: RootState) => state.global.locations,
  selectMachinesToDisplay,
  (locations, machines) => {
    const locationIds = new Set(locations.map((location) => location.id));
    const isUnassignedLocation = (location: { id: string; name: string }) =>
      location.id === constants.unassignedMachinesLocation.id ||
      location.name === t(`label-Unassigned Machines`);
    const isMachineUnassigned = (machine: { locationId?: string }) =>
      !machine.locationId || !locationIds.has(machine.locationId);

    return locations
      .map((location) => ({
        ...location,
        machines: machines.filter((m) =>
          isUnassignedLocation(location)
            ? isMachineUnassigned(m)
            : m.locationId === location.id
        ),
      }))
      .filter((location) => {
        return location.machines.length > 0;
      });
  }
);

export const selectMachinesWithSparklines = createSelector(
  selectMachinesToDisplay,
  (state: RootState) => state.currentStatus.machineCarouselData,
  (machines, machineCarouselData) =>
    machines
      .filter(
        (machine) =>
          machineCarouselData[machine.id] &&
          machineCarouselData[machine.id]!.length > 0
      )
      .map(({ id }) => id)
);

export const selectMachinesWithAlerts = createSelector(
  selectMachinesToDisplay,
  (state: RootState) => state.currentStatus.machineAlerts,
  (state: RootState) => state.filter.active.alertLevel,
  (machines, alertData, activeAlertLevels) =>
    machines
      .filter(
        (machine) =>
          alertData[machine.id] &&
          getActiveNotClearedAlerts(
            getAlertsByLevel(alertData[machine.id] || [], activeAlertLevels)
          ).length > 0
      )
      .map(({ id }) => id)
);

export const selectLastFetchedCarouselData = createSelector(
  (state: RootState) => state.currentStatus.lastFetchedCarouselData,
  (x) => x
);

/** The last fetched cycle time is the last time results were received,
 * because there are no created dates in the cycle time data,
 * and it can't be concatenated.
 */
export const selectLastFetchedCycleTime = createSelector(
  (state: RootState) => state.currentStatus.lastFetchedCycleTime,
  (x) => x
);

export const selectLastFetchedForRefresh = (s: RootState) =>
  s.currentStatus.lastFetchedForRefresh;
